import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Megaphone, Copy, Check, Trash2, Plus, Activity,
  Loader2, CheckCircle2, XCircle, BarChart3, Crown, ExternalLink
} from "lucide-react";
import { getMainDomainUrl } from "@/utils/subdomain";
import { useLanguage } from "@/contexts/LanguageContext";

const STR = {
  en: {
    title: "Promo", subtitle: "Campaign links, link health and tracking pixels",
    back: "Back", campaigns: "Campaign links",
    campaignsHint: "Create trackable links to measure where your traffic comes from.",
    name: "Name", source: "Source (e.g. instagram)", medium: "Medium (e.g. story)",
    create: "Create", noCampaigns: "No campaigns yet", views: "Views", clicks: "Clicks",
    copy: "Copy", copied: "Copied", delete: "Delete",
    health: "Link health", healthHint: "Check that all your platform links are reachable.",
    runCheck: "Run check", checking: "Checking...", working: "Working", broken: "Broken",
    notChecked: "Not checked", pixels: "Tracking pixels",
    pixelsHint: "Add marketing pixels to your public page. Pro only.",
    save: "Save", saved: "Saved", proOnly: "Available on Pro plan",
    openPage: "Open page",
  },
  ru: {
    title: "Промо", subtitle: "Промо-ссылки, проверка ссылок и пиксели",
    back: "Назад", campaigns: "Промо-ссылки",
    campaignsHint: "Создавайте отслеживаемые ссылки, чтобы видеть, откуда приходит трафик.",
    name: "Название", source: "Источник (напр. instagram)", medium: "Канал (напр. story)",
    create: "Создать", noCampaigns: "Пока нет кампаний", views: "Просмотры", clicks: "Клики",
    copy: "Копировать", copied: "Скопировано", delete: "Удалить",
    health: "Проверка ссылок", healthHint: "Проверьте, что все ссылки на площадки работают.",
    runCheck: "Проверить", checking: "Проверка...", working: "Работает", broken: "Не работает",
    notChecked: "Не проверено", pixels: "Пиксели отслеживания",
    pixelsHint: "Добавьте маркетинговые пиксели на публичную страницу. Только для Pro.",
    save: "Сохранить", saved: "Сохранено", proOnly: "Доступно на тарифе Pro",
    openPage: "Открыть страницу",
  },
  es: {
    title: "Promo", subtitle: "Enlaces de campaña, estado de enlaces y píxeles",
    back: "Atrás", campaigns: "Enlaces de campaña",
    campaignsHint: "Crea enlaces medibles para saber de dónde viene tu tráfico.",
    name: "Nombre", source: "Fuente (p. ej. instagram)", medium: "Medio (p. ej. story)",
    create: "Crear", noCampaigns: "Aún no hay campañas", views: "Vistas", clicks: "Clics",
    copy: "Copiar", copied: "Copiado", delete: "Eliminar",
    health: "Estado de enlaces", healthHint: "Comprueba que todos tus enlaces funcionan.",
    runCheck: "Comprobar", checking: "Comprobando...", working: "Funciona", broken: "Roto",
    notChecked: "Sin comprobar", pixels: "Píxeles de seguimiento",
    pixelsHint: "Añade píxeles de marketing a tu página pública. Solo Pro.",
    save: "Guardar", saved: "Guardado", proOnly: "Disponible en el plan Pro",
    openPage: "Abrir página",
  },
};

export default function PromoPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const s = STR[language] || STR.en;

  const platformName = (id) => t('platforms', id) || id;

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [newCampaign, setNewCampaign] = useState({ name: "", source: "", medium: "" });
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [healthResults, setHealthResults] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [pixels, setPixels] = useState({ facebook: "", google: "", tiktok: "", vk: "", yandex: "" });
  const [savingPixels, setSavingPixels] = useState(false);

  const isPro = Boolean(user?.plan_config?.can_remove_branding) || user?.plan === "pro" ||
    ["owner", "admin"].includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get(`/pages/${pageId}`);
        setPage(resp.data);
        setPixels({
          facebook: resp.data.pixels?.facebook || "",
          google: resp.data.pixels?.google || "",
          tiktok: resp.data.pixels?.tiktok || "",
          vk: resp.data.pixels?.vk || "",
          yandex: resp.data.pixels?.yandex || "",
        });
        const camp = await api.get(`/pages/${resp.data.id}/campaigns`);
        setCampaigns(camp.data || []);
      } catch (e) {
        toast.error("Failed to load");
        navigate("/multilinks");
      } finally {
        setLoading(false);
      }
    })();
  }, [pageId, navigate]);

  const effectiveSlug = page?.custom_slug?.trim() || page?.slug;

  const campaignUrl = (c) => {
    const base = getMainDomainUrl(`/${effectiveSlug}`);
    const params = new URLSearchParams({ c: c.id });
    if (c.source) params.set("utm_source", c.source);
    if (c.medium) params.set("utm_medium", c.medium);
    if (c.campaign || c.name) params.set("utm_campaign", c.campaign || c.name);
    return `${base}?${params.toString()}`;
  };

  const createCampaign = async () => {
    if (!newCampaign.name.trim()) { toast.error(s.name); return; }
    setCreating(true);
    try {
      const resp = await api.post(`/pages/${page.id}/campaigns`, {
        name: newCampaign.name,
        source: newCampaign.source,
        medium: newCampaign.medium,
        campaign: newCampaign.name,
      });
      setCampaigns(prev => [resp.data, ...prev]);
      setNewCampaign({ name: "", source: "", medium: "" });
    } catch (e) {
      toast.error("Error");
    } finally {
      setCreating(false);
    }
  };

  const deleteCampaign = async (id) => {
    try {
      await api.delete(`/pages/${page.id}/campaigns/${id}`);
      setCampaigns(prev => prev.filter(c => c.id !== id));
    } catch (e) { toast.error("Error"); }
  };

  const copyCampaign = (c) => {
    navigator.clipboard.writeText(campaignUrl(c));
    setCopiedId(c.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success(s.copied);
  };

  const runHealthCheck = async () => {
    setCheckingHealth(true);
    try {
      const resp = await api.post(`/pages/${page.id}/links/health`);
      setHealthResults(resp.data.results || {});
    } catch (e) {
      toast.error("Error");
    } finally {
      setCheckingHealth(false);
    }
  };

  const savePixels = async () => {
    setSavingPixels(true);
    try {
      await api.put(`/pages/${page.id}`, { pixels });
      toast.success(s.saved);
    } catch (e) {
      toast.error("Error");
    } finally {
      setSavingPixels(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Sidebar>
    );
  }

  const links = page?.links || [];

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`/multilinks/${effectiveSlug}/edit`)} className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-white/5 hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" /> {s.title}
            </h1>
            <p className="text-sm text-muted-foreground">{page?.artist_name} — {page?.release_title}</p>
          </div>
          <a href={getMainDomainUrl(`/${effectiveSlug}`)} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="w-4 h-4 mr-2" />{s.openPage}</Button>
          </a>
        </div>

        {/* Campaign links */}
        <section className="p-5 rounded-2xl panel-card mb-6">
          <h2 className="font-semibold mb-1 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" />{s.campaigns}</h2>
          <p className="text-xs text-muted-foreground mb-4">{s.campaignsHint}</p>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
            <Input placeholder={s.name} value={newCampaign.name} onChange={(e) => setNewCampaign(p => ({ ...p, name: e.target.value }))} className="bg-card" />
            <Input placeholder={s.source} value={newCampaign.source} onChange={(e) => setNewCampaign(p => ({ ...p, source: e.target.value }))} className="bg-card" />
            <Input placeholder={s.medium} value={newCampaign.medium} onChange={(e) => setNewCampaign(p => ({ ...p, medium: e.target.value }))} className="bg-card" />
            <Button onClick={createCampaign} disabled={creating} className="bg-primary hover:bg-primary/90">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />{s.create}</>}
            </Button>
          </div>

          {campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{s.noCampaigns}</p>
          ) : (
            <div className="space-y-2">
              {campaigns.map(c => (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{campaignUrl(c)}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{s.views}: {c.views || 0} · {s.clicks}: {c.clicks || 0}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyCampaign(c)}>
                    {copiedId === c.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400" onClick={() => deleteCampaign(c.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Link health */}
        <section className="p-5 rounded-2xl panel-card mb-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-primary" />{s.health}</h2>
            <Button onClick={runHealthCheck} disabled={checkingHealth} variant="outline" size="sm" className="rounded-full">
              {checkingHealth ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{s.checking}</> : s.runCheck}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">{s.healthHint}</p>
          <div className="space-y-2">
            {links.map(l => {
              const res = healthResults?.[l.id];
              const ok = res ? res.ok : (l.health_ok ?? null);
              return (
                <div key={l.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border">
                  <span className="flex-1 text-sm truncate">{platformName(l.platform)}</span>
                  <span className="text-xs text-muted-foreground truncate max-w-[40%] hidden sm:block">{l.url}</span>
                  {ok === null ? (
                    <span className="text-xs text-muted-foreground">{s.notChecked}</span>
                  ) : ok ? (
                    <span className="flex items-center gap-1 text-xs text-green-500"><CheckCircle2 className="w-4 h-4" />{s.working}</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-red-400"><XCircle className="w-4 h-4" />{s.broken}</span>
                  )}
                </div>
              );
            })}
            {links.length === 0 && <p className="text-sm text-muted-foreground py-2 text-center">—</p>}
          </div>
        </section>

        {/* Pixels */}
        <section className="p-5 rounded-2xl panel-card">
          <h2 className="font-semibold mb-1 flex items-center gap-2">
            {s.pixels}
            {!isPro && <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-500"><Crown className="w-3 h-3" />PRO</span>}
          </h2>
          <p className="text-xs text-muted-foreground mb-4">{s.pixelsHint}</p>
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${!isPro ? 'opacity-50 pointer-events-none' : ''}`}>
            <div><Label className="text-xs">Meta (Facebook) Pixel ID</Label><Input value={pixels.facebook} onChange={(e) => setPixels(p => ({ ...p, facebook: e.target.value }))} className="mt-1 bg-card" placeholder="123456789012345" /></div>
            <div><Label className="text-xs">Google Analytics / GTM ID</Label><Input value={pixels.google} onChange={(e) => setPixels(p => ({ ...p, google: e.target.value }))} className="mt-1 bg-card" placeholder="G-XXXXXXX / GTM-XXXX" /></div>
            <div><Label className="text-xs">TikTok Pixel ID</Label><Input value={pixels.tiktok} onChange={(e) => setPixels(p => ({ ...p, tiktok: e.target.value }))} className="mt-1 bg-card" placeholder="CXXXXXXXXXXXXXXXXXXX" /></div>
            <div><Label className="text-xs">Yandex Metrika ID</Label><Input value={pixels.yandex} onChange={(e) => setPixels(p => ({ ...p, yandex: e.target.value }))} className="mt-1 bg-card" placeholder="12345678" /></div>
            <div><Label className="text-xs">VK / top.mail.ru counter</Label><Input value={pixels.vk} onChange={(e) => setPixels(p => ({ ...p, vk: e.target.value }))} className="mt-1 bg-card" placeholder="1234567" /></div>
          </div>
          {!isPro ? (
            <p className="text-xs text-yellow-500 mt-3">{s.proOnly}</p>
          ) : (
            <Button onClick={savePixels} disabled={savingPixels} className="mt-4 bg-primary hover:bg-primary/90">
              {savingPixels ? <Loader2 className="w-4 h-4 animate-spin" /> : s.save}
            </Button>
          )}
        </section>
      </div>
    </Sidebar>
  );
}
